import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _size from 'lodash/size';
import PropTypes from 'prop-types';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// Models
import mPlanning from 'models/planning';
// Custom
import {
  refreshList, getInstallationPlannings, updateInstallationPlanning, deleteInstallationPlanning,
} from 'actions';
import { planningsShape, deleteShape } from 'constants/PropTypesShapes';
import { computeErrors } from 'utils/Forms';
import { planningsAddPath, customersPath, navigateToPlanningsShow } from 'installations/navigation';
import SubListTemplate from 'components/templates/SubListTemplate';
import { AddTextButton, CustomersTextButton } from 'components/buttons/TextButtons';
import withPermissions from 'hocs/WithPermissions';
import withConfirmDialog from 'hocs/WithConfirmDialog';
import withMessageDialog from 'hocs/WithMessageDialog';
import withEntitiesInformation from 'installations/assets/hocs/WithEntitiesInformation';
import withStateReinit from 'hocs/WithStateReinit';
import withResources from 'installations/assets/hocs/WithResources';
import PlanningRow from 'installations/assets/plannings/PlanningRow';
import PlanningRowDetails from 'installations/assets/plannings/PlanningRowDetails';

class Plannings extends React.Component {
  constructor(props) {
    super(props);

    this.handleUpdate = this.handleUpdate.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleRowDbleClick = this.handleRowDbleClick.bind(this);
    this.doGetRowComp = this.doGetRowComp.bind(this);
    this.doGetRowCompDetails = this.doGetRowCompDetails.bind(this);
  }

  componentDidUpdate(prevProps) {
    const {
      updated, planningDelete,
      handleDisplayMessage, handleRefreshList,
    } = this.props;

    // Operations catching
    if (
      (!prevProps.updated && updated)
      || (!prevProps.planningDelete.deleted && planningDelete.deleted)
    ) {
      handleRefreshList();
    }

    // Check errors
    if (planningDelete.error && !prevProps.planningDelete.error) {
      // Compute errors
      const errors = computeErrors(null, planningDelete.error);
      // Display message
      handleDisplayMessage(`error.${errors.global}`, {}, 'warning');
    }
  }

  // Handlers
  handleUpdate(planning, data) {
    const { params, handleUpdatePlanning } = this.props;
    handleUpdatePlanning(params.id, planning.id, data);
  }

  handleDelete(planning) {
    const { params, handleDisplayConfirm, handleDeletePlanning } = this.props;
    handleDisplayConfirm(
      { id: 'confirm.deletePlanning', values: { planningName: planning.name } },
      () => handleDeletePlanning(params.id, planning.id),
    );
  }

  handleRowDbleClick(planning) {
    const { params, hasPermission } = this.props;
    const { pInstallation: { planningWrite } } = permissions;
    if (planning && hasPermission({ needed: planningWrite, permissionsOrContext: 'installationPermissions' })) {
      // Display planning
      navigateToPlanningsShow(params.id, planning.id);
    }
  }

  // Private
  doGetRowComp(props) {
    const { params, hasMultipleEntities } = this.props;
    return (
      <PlanningRow
        {...props}
        id={params.id}
        withEntity={hasMultipleEntities}
        onDelete={this.handleDelete}
      />
    );
  }

  doGetRowCompDetails(props) {
    const { hasPermission } = this.props;

    // Permissions
    const { pInstallation: { planningWrite } } = permissions;

    return (
      <PlanningRowDetails
        {...props}
        editable={hasPermission({ needed: planningWrite, permissionsOrContext: 'installationPermissions' })}
        onUpdate={this.handleUpdate}
      />
    );
  }

  render() {
    const {
      params, plannings, desynchronized, hasPermission, hasMultipleEntities, handleGetPlannings,
    } = this.props;
    const sortedPlannings = mPlanning.sortElements(plannings.elements);

    // For the sub list, create the good structure
    const planningsList = {
      loading: plannings.loading,
      elements: {
        data: sortedPlannings,
        totalEntries: _size(sortedPlannings),
      },
    };

    // Permissions
    const { pInstallation: { planningWrite, entityRead } } = permissions;

    return (
      <SubListTemplate
        title={<LocalizedMessage id="title.planningsList" />}
        displayRefresh
        desynchronized={desynchronized}
        actionButtons={[
          <OnlyWith
            key="bAdd"
            permissions={{
              needed: `${planningWrite}&&${entityRead}`,
              permissionsOrContext: 'installationPermissions',
            }}
          >
            <AddTextButton primary path={planningsAddPath(params.id)} />
          </OnlyWith>,
        ]}
        extraActionButtons={[<CustomersTextButton key="bCus" primary path={customersPath(params.id)} />]}
        name={mPlanning.name}
        listFeatures={mPlanning.listFeatures({ withEntity: hasMultipleEntities })}
        withQueryParams={false}
        withDetails
        withActions={hasPermission({ needed: planningWrite, permissionsOrContext: 'installationPermissions' })}
        nbActions={2}
        elementsData={{
          loading: planningsList.loading,
          elements: planningsList.elements,
          handleGetList: (p, s, f, firstLoad) => handleGetPlannings(params.id, firstLoad),
        }}
        onRowDbleClick={this.handleRowDbleClick}
        withPagination={false}
        withVirtualization={false}
        rowComp={this.doGetRowComp}
        rowCompDetails={this.doGetRowCompDetails}
      />
    );
  }
}

Plannings.propTypes = {
  params: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  plannings: planningsShape.isRequired,
  updated: PropTypes.bool.isRequired,
  planningDelete: deleteShape.isRequired,
  desynchronized: PropTypes.bool.isRequired,
  hasPermission: PropTypes.func.isRequired,
  hasMultipleEntities: PropTypes.bool.isRequired,
  handleDisplayConfirm: PropTypes.func.isRequired,
  handleDisplayMessage: PropTypes.func.isRequired,
  handleRefreshList: PropTypes.func.isRequired,
  handleGetPlannings: PropTypes.func.isRequired,
  handleUpdatePlanning: PropTypes.func.isRequired,
  handleDeletePlanning: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  plannings: state.installationPlanning.list,
  updated: state.installationPlanning.update.updated,
  planningDelete: state.installationPlanning.delete,
  desynchronized: state.installationPlanning.list.desynchronized,
});

const mapDispatchToProps = (dispatch) => ({
  handleRefreshList: () => {
    dispatch(refreshList());
  },
  handleGetPlannings: (id, firstLoad) => {
    dispatch(getInstallationPlannings(id, firstLoad));
  },
  handleUpdatePlanning: (id, idPlanning, data) => {
    dispatch(updateInstallationPlanning(id, idPlanning, data));
  },
  handleDeletePlanning: (id, idPlanning) => {
    dispatch(deleteInstallationPlanning(id, idPlanning));
  },
});

module.exports = compose(
  withResources(
    (ownProps) => ({ id: ownProps.params.id }),
    ['devices'],
  ),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withPermissions,
  withConfirmDialog,
  withMessageDialog,
  withEntitiesInformation,
  withStateReinit(['deleteInstallationPlanning']),
)(Plannings);



// WEBPACK FOOTER //
// ./routes/app/routes/installations/routes/show/routes/plannings/routes/index/components/InstallationPlannings.js